import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, Globe, MapPin, ArrowLeft, MoreHorizontal,
  DollarSign, RefreshCw, Shield, FileDown, FileText, Sparkles, Gauge, Flame,
} from "lucide-react";

export function CompanyHeader({
  company, user, team, assignedName, latestAudit,
  analyzing, auditing, syncing, downloading,
  navigate, assignTo, updateStatus, deepAnalyze, runAudit, downloadReport, syncCRM,
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Button data-testid="back-btn" variant="ghost" size="sm" onClick={() => navigate("/pipeline")} className="h-8 w-8 p-0">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Building2 className="w-5 h-5 text-slate-500" /></div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{company.name}</h1>
            <Badge variant="outline" className={`capitalize text-[9px] h-4 ${
              company.status === "qualified" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
              company.status === "contacted" ? "bg-amber-50 text-amber-700 border-amber-200" :
              "bg-indigo-50 text-indigo-700 border-indigo-200"
            }`}>{company.status}</Badge>
            {(company.priority_score || 0) >= 80 && (
              <Badge data-testid="hot-lead-badge" className="text-[9px] h-4 bg-amber-500 text-white border-0 flex items-center gap-0.5">
                <Flame className="w-2.5 h-2.5" />Hot Lead
              </Badge>
            )}
            {company.assignment_status === "unassigned" && (
              <Badge data-testid="unassigned-badge" variant="outline" className="text-[9px] h-4 bg-rose-50 text-rose-700 border-rose-200">Unassigned Lead</Badge>
            )}
            {assignedName && (
              <Badge variant="outline" className="text-[9px] h-4 bg-slate-50 text-slate-600 border-slate-200">Owner: {assignedName}</Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
            <Badge variant="outline" className="text-[9px] h-4">{company.category}</Badge>
            {company.domain && <span className="flex items-center gap-0.5"><Globe className="w-3 h-3" />{company.domain}</span>}
            <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{company.city}, {company.state}</span>
            {company.revenue_label && <span className="flex items-center gap-0.5"><DollarSign className="w-3 h-3" />{company.revenue_label}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        {user?.role === "admin" && team.length > 0 && (
          <Select value={company.assigned_to || ""} onValueChange={assignTo}>
            <SelectTrigger data-testid="assign-select" className="w-32 h-7 text-[11px]">
              <SelectValue placeholder="Assign to…" />
            </SelectTrigger>
            <SelectContent>
              {team.map((t) => (
                <SelectItem key={t.user_id} value={t.user_id}>{t.name || t.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={company.status} onValueChange={updateStatus}>
          <SelectTrigger data-testid="status-select" className="w-28 h-7 text-[11px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="qualified">Qualified</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="deep-analyze-btn" onClick={deepAnalyze} disabled={analyzing} size="sm" className="h-7 text-[10px] text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700" title="Multi-source AI deep research → full Prospect Dossier (free, ~1 min)">
          {analyzing ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />} {company.dossier ? "Re-analyze" : "Deep Analyze"}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="actions-menu-btn" size="sm" variant="outline" className="h-7 px-2 text-[10px]"><MoreHorizontal className="w-3.5 h-3.5" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-[10px]">Audit</DropdownMenuLabel>
            <DropdownMenuItem data-testid="run-audit-btn" onClick={runAudit} disabled={auditing} className="text-xs">
              <Gauge className="w-3.5 h-3.5 mr-2" /> Quick Audit (scores)
            </DropdownMenuItem>
            {latestAudit && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px]">Report</DropdownMenuLabel>
                <DropdownMenuItem data-testid="download-pdf-btn" onClick={() => downloadReport("pdf", latestAudit.audit_id)} disabled={!!downloading} className="text-xs">
                  <FileDown className="w-3.5 h-3.5 mr-2" /> Download Report PDF
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="download-html-btn" onClick={() => downloadReport("html", latestAudit.audit_id)} disabled={!!downloading} className="text-xs">
                  <FileText className="w-3.5 h-3.5 mr-2" /> View HTML Report
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px]">Sync to CRM</DropdownMenuLabel>
            <DropdownMenuItem data-testid="sync-monday" onClick={() => syncCRM("monday")} disabled={syncing} className="text-xs">
              <Shield className="w-3.5 h-3.5 mr-2" /> Monday Sales CRM
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="sync-ghl" onClick={() => syncCRM("gohighlevel")} disabled={syncing} className="text-xs">
              <Shield className="w-3.5 h-3.5 mr-2" /> GoHighLevel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
